import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import Button from '@/components/Button';
import Card from '@/components/Card';
import Chip from '@/components/Chip';
import EmptyState from '@/components/EmptyState';
import LoadingState from '@/components/LoadingState';
import Modal from '@/components/Modal';
import Page from '@/components/Page';
import Skeleton from '@/components/Skeleton';
import Stack from '@/components/Stack';
import Table from '@/components/Table';
import TableHeader from '@/components/TableHeader';
import Tabs from '@/components/Tabs';
import { Body, Heading, Small } from '@/components/Typography';
import { TableCell, TableRow } from '@/elements/Table';
import Link from '@/elements/Link';
import useMediaQuery from '@/hooks/useMediaQuery';
import ROUTES from '@/routes/routes';
import BREAKPOINTS from '@/styles/breakpoints';

import {
  useAccountDetail,
  useAssignTimesheets,
  useCycleAccountWorkspace,
  useCycleAnalysis,
  useCreateInvoice,
  useCycleAccounts,
  useCycles,
  useEmptyDraftInvoices,
  useReconcile,
  useResolveStuckHeldTimesheet,
  useRemoveLineItem,
  useSoftDeleteEmptyDraftInvoice,
  useStuckHeldTimesheets,
  useVoidInvoice,
} from './hooks/useInvoiceHealth';
import type {
  ReconcileAction,
  ReconcilePreview,
} from './hooks/useInvoiceHealth';
import type {
  CycleAccount,
  CycleAccountsData,
  CycleAccountWorkspace,
  CycleAnalysisResult,
  CycleInvoiceSection,
  CycleTimesheet,
  CreateInvoiceGroup,
  DeepAccountDetail,
  EmptyDraftInvoice,
  EmptyDraftInvoicesResult,
  InvoiceCycle,
  StuckHeldTimesheet,
  StuckHeldTimesheetsResult,
} from './types';

const humanizeStatus = (status: string) =>
  status.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

const humanizeLabel = (value: string) =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatDateTime = (value: string | null) => {
  if (!value) return 'n/a';

  return new Date(value).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatCurrency = (amountCents: number | null) => {
  if (amountCents == null) return 'n/a';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amountCents / 100);
};

const statusColor = (
  status: string,
): 'success' | 'warning' | 'error' | 'default' => {
  if (status === 'included') return 'success';
  if (status === 'not_included') return 'warning';
  if (
    status === 'requires_review' ||
    status === 'rejected' ||
    status === 'not_invoiceable' ||
    status === 'held'
  ) {
    return 'error';
  }

  return 'default';
};

const linkedTextCss = {
  color: '#16a34a',
  textDecoration: 'underline',
  fontWeight: 600,
};

const flattenAccountTimesheets = (account: CycleAccountWorkspace) => {
  const seen = new Set<number>();
  const rows: CycleTimesheet[] = [];

  account.invoices.forEach((invoice) => {
    invoice.timesheets.forEach((timesheet) => {
      if (seen.has(timesheet.id)) return;
      seen.add(timesheet.id);
      rows.push(timesheet);
    });
  });

  account.unassigned_timesheets.forEach((timesheet) => {
    if (seen.has(timesheet.id)) return;
    seen.add(timesheet.id);
    rows.push(timesheet);
  });

  return rows;
};

const INVOICE_HEALTH_PASSWORD = 'gravydevs123';
const INVOICE_HEALTH_AUTH_KEY = 'invoiceHealthAuth';

type AccountFilter = 'all' | 'complete' | 'incomplete' | 'no_invoice';
type WorkspaceTab = 'accounts' | 'diagnostics';

const parseInteger = (value: string | null) => {
  if (!value) return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const accountHasWorkspaceData = (
  account: CycleAccount | null | undefined,
): account is CycleAccountWorkspace =>
  Boolean(account?.invoices && account?.unassigned_timesheets);

const accountInvoiceCount = (account: CycleAccount) =>
  account.invoice_count ?? account.invoices?.length ?? 0;

const actionableMissingCount = (account: CycleAccount) =>
  account.ready_to_assign_count ??
  account.unassigned_timesheets?.filter(
    (timesheet) => timesheet.status === 'not_included',
  ).length ??
  account.missing_timesheets_count;

const conflictedGroupCount = (account: CycleAccount) =>
  account.conflicted_group_count ?? (account.conflicted_groups || []).length;

const accountIsComplete = (account: CycleAccount) =>
  accountInvoiceCount(account) > 0 &&
  conflictedGroupCount(account) === 0 &&
  actionableMissingCount(account) === 0;

const accountStatusMeta = (account: CycleAccount) => {
  if (account.status) {
    if (account.status === 'conflicted') {
      return {
        color: 'error' as const,
        label: 'Conflicted',
        priority: 0,
      };
    }

    if (account.status === 'no_invoice') {
      return {
        color: 'warning' as const,
        label: 'No invoice',
        priority: 1,
      };
    }

    if (account.status === 'needs_action') {
      return {
        color: 'warning' as const,
        label: 'Needs action',
        priority: 2,
      };
    }

    return {
      color: 'success' as const,
      label: 'Complete',
      priority: 3,
    };
  }

  if (conflictedGroupCount(account) > 0) {
    return {
      color: 'error' as const,
      label: 'Conflicted',
      priority: 0,
    };
  }

  if (accountInvoiceCount(account) === 0) {
    return {
      color: 'warning' as const,
      label: 'No invoice',
      priority: 1,
    };
  }

  if (actionableMissingCount(account) > 0) {
    return {
      color: 'warning' as const,
      label: 'Needs action',
      priority: 2,
    };
  }

  return {
    color: 'success' as const,
    label: 'Complete',
    priority: 3,
  };
};

const workspaceCacheKey = (cycleId: number, accountId: number) =>
  `${cycleId}:${accountId}`;

type CycleDropdownProps = {
  cycles: InvoiceCycle[];
  selectedCycle: InvoiceCycle | null;
  cycleData?: CycleAccountsData | null;
  onSelect: (cycle: InvoiceCycle) => void;
};

const CycleDropdown: React.FC<CycleDropdownProps> = ({
  cycles,
  selectedCycle,
  cycleData,
  onSelect,
}) => {
  const selected = selectedCycle;
  const selectedLoadedStats = useMemo(() => {
    if (!selected || !cycleData) return null;

    return {
      invoiceCount: cycleData.accounts.reduce(
        (sum, account) => sum + accountInvoiceCount(account),
        0,
      ),
      timesheetCount: cycleData.summary.timesheets_count,
      failedAccountCount: selected.failed_account_count,
    };
  }, [cycleData, selected]);

  const displayInvoiceCount =
    selectedLoadedStats?.invoiceCount ?? selected?.invoice_count ?? 0;
  const displayTimesheetCount =
    selectedLoadedStats?.timesheetCount ?? selected?.timesheet_count ?? 0;
  const displayFailedAccountCount =
    selectedLoadedStats?.failedAccountCount ??
    selected?.failed_account_count ??
    0;

  return (
    <Stack vertical gap={8}>
      <div style={{ minWidth: 320 }}>
        <select
          value={selected?.id ?? ''}
          onChange={(event) => {
            const cycle = cycles.find(
              (entry) => entry.id === Number(event.target.value),
            );
            if (cycle) onSelect(cycle);
          }}
          style={{
            width: '100%',
            padding: '10px 14px',
            borderRadius: 8,
            border: '1px solid #d0d5dd',
            background: '#fff',
            fontSize: 14,
            fontWeight: 500,
            color: '#344054',
            cursor: 'pointer',
            appearance: 'none',
          }}
        >
          <option value="" disabled>
            Select an invoice cycle...
          </option>
          {cycles.map((cycle) => (
            <option key={cycle.id} value={cycle.id}>
              {cycle.is_preview ? 'Future: ' : ''}
              {cycle.date_label} ({cycle.day_label})
            </option>
          ))}
        </select>
      </div>

      {selected && (
        <Stack vertical gap={4}>
          <Stack gap={8} align="center" wrap>
            <Small>
              {displayInvoiceCount} invoices and {displayTimesheetCount}{' '}
              timesheets in cycle
            </Small>
            {displayFailedAccountCount > 0 && (
              <div style={{ color: '#b42318', fontSize: 12 }}>
                {displayFailedAccountCount} failed accounts
              </div>
            )}
          </Stack>
          {selected.is_preview && (
            <Small>
              Previewing next week&apos;s run based on{' '}
              {selected.preview_base_label || 'the selected cycle'}
            </Small>
          )}
        </Stack>
      )}
    </Stack>
  );
};

type ConfirmCreateState = {
  account: CycleAccount;
} | null;

type ConfirmCreateModalProps = {
  state: ConfirmCreateState;
  groups: CreateInvoiceGroup[];
  loading: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (groupKeys: string[]) => void;
};

const ConfirmCreateInvoiceModal: React.FC<ConfirmCreateModalProps> = ({
  state,
  groups,
  loading,
  error,
  onCancel,
  onConfirm,
}) => {
  const [selectedGroupKeys, setSelectedGroupKeys] = useState<string[]>([]);

  useEffect(() => {
    setSelectedGroupKeys(groups.map((group) => group.group_key));
  }, [groups, state]);

  if (!state) return null;

  const toggleGroup = (groupKey: string) => {
    setSelectedGroupKeys((current) =>
      current.includes(groupKey)
        ? current.filter((value) => value !== groupKey)
        : [...current, groupKey],
    );
  };

  return (
    <Modal
      displayFlex
      size="md"
      title="Create grouped invoices"
      onRequestClose={loading ? undefined : onCancel}
      wrapperBackground
    >
      <Card>
        <Card.Section>
          <Stack vertical gap={16}>
            <Body weight="semibold">
              Choose which missing invoice groups to create for{' '}
              {state.account.account_name}.
            </Body>
            <Small>
              Invoice creation follows the account&apos;s existing grouping
              rules. Each selected group will create one invoice bucket for the
              current cycle.
            </Small>
            {groups.length === 0 ? (
              <EmptyState title="No missing invoice groups to create" />
            ) : (
              <Stack vertical gap={10}>
                {groups.map((group) => (
                  <label
                    key={group.group_key}
                    style={{
                      display: 'flex',
                      gap: 12,
                      alignItems: 'flex-start',
                      padding: 12,
                      border: '1px solid #e4e7ec',
                      borderRadius: 10,
                      background: '#f8fafc',
                    }}
                  >
                    <input
                      checked={selectedGroupKeys.includes(group.group_key)}
                      disabled={loading}
                      type="checkbox"
                      onChange={() => toggleGroup(group.group_key)}
                    />
                    <Stack vertical gap={4} css={{ flex: 1 }}>
                      <Body weight="semibold">{group.group_label}</Body>
                      <Small>
                        {group.timesheets_count} timesheets •{' '}
                        {formatCurrency(group.total_amount_cents)}
                      </Small>
                    </Stack>
                  </label>
                ))}
              </Stack>
            )}
            {error && (
              <div style={{ color: '#b42318', fontSize: 14 }}>{error}</div>
            )}
            <Stack justify="end" gap={12}>
              <Button
                a11yLabel="Cancel"
                appearance="outline"
                label="Cancel"
                disabled={loading}
                type="button"
                onClick={onCancel}
              />
              <Button
                a11yLabel="Confirm create invoice"
                label="Create selected"
                disabled={groups.length === 0 || selectedGroupKeys.length === 0}
                isLoading={loading}
                type="button"
                onClick={() => onConfirm(selectedGroupKeys)}
              />
            </Stack>
          </Stack>
        </Card.Section>
      </Card>
    </Modal>
  );
};

const ACTION_LABELS: Record<string, string> = {
  void_duplicate: 'Void duplicate invoice',
  fix_totals: 'Sync totals from Stripe',
  items_missing_locally: 'Create missing local items',
  items_orphaned_locally: 'Remove orphaned local items',
  fix_item_amount: 'Fix item amount mismatch',
  stripe_invoice_not_found: 'Stripe invoice not found',
  error: 'Error during reconciliation',
};

const actionLabel = (action: string) =>
  ACTION_LABELS[action] || humanizeLabel(action);

const formatReconcileDetailValue = (value: unknown) => {
  if (value == null) return 'n/a';
  if (Array.isArray(value)) {
    return value.map((entry) => formatReconcileDetailValue(entry)).join(', ');
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
};

type ReconcileModalProps = {
  isOpen: boolean;
  accountName: string | null;
  accountId: number | null;
  preview: ReconcilePreview | null;
  loading: boolean;
  executing: boolean;
  error: string | null;
  onClose: () => void;
  onExecute: () => void;
};

const ReconcileModal: React.FC<ReconcileModalProps> = ({
  isOpen,
  accountName,
  accountId,
  preview,
  loading,
  executing,
  error,
  onClose,
  onExecute,
}) => {
  if (!isOpen) return null;

  const actions = preview?.actions || [];
  const hasActions = actions.length > 0;
  const isExecuted = preview?.mode === 'execute';

  return (
    <Modal
      disableClickout
      size="lg"
      title={`Reconcile ${accountName || `Account ${accountId}`}`}
      onRequestClose={executing ? undefined : onClose}
    >
      <Card>
        <Card.Section>
          <Stack vertical gap={6}>
            <Body weight="semibold">
              {isExecuted
                ? 'Reconciliation completed'
                : 'Review the Stripe sync preview before making changes'}
            </Body>
            <Small>
              {isExecuted
                ? 'Executed actions are listed below so you can review what changed.'
                : 'This checks the selected account against Stripe and proposes safe repair actions before you execute them.'}
            </Small>
          </Stack>
        </Card.Section>

        <Card.Section>
          {loading && (
            <LoadingState text="Scanning invoices against Stripe..." />
          )}
          {!loading && error && (
            <div style={{ color: '#b42318', fontSize: 14 }}>{error}</div>
          )}
          {!loading && preview && (
            <Stack vertical gap={16}>
              <Card>
                <Card.Section>
                  <Stack vertical gap={10}>
                    <Body weight="semibold">Summary</Body>
                    <Stack gap={8} wrap>
                      <Chip
                        color="default"
                        label={`${preview.invoices_scanned} invoices scanned`}
                        size="small"
                      />
                      {Object.entries(preview.stats).map(([key, value]) =>
                        value > 0 ? (
                          <Chip
                            key={key}
                            color="default"
                            label={`${humanizeLabel(key)} ${value}`}
                            size="small"
                          />
                        ) : null,
                      )}
                    </Stack>
                    <Small>
                      Mode:{' '}
                      {isExecuted ? 'Executed changes' : 'Dry run preview only'}
                    </Small>
                  </Stack>
                </Card.Section>
              </Card>

              {!hasActions && (
                <Card>
                  <Card.Section>
                    <EmptyState title="No reconciliation actions needed — everything is in sync" />
                  </Card.Section>
                </Card>
              )}

              {hasActions && (
                <Card>
                  <Card.Section>
                    <Body weight="semibold">
                      {isExecuted ? 'Actions executed' : 'Proposed actions'} (
                      {actions.length})
                    </Body>
                  </Card.Section>
                  <Card.Section>
                    <Stack vertical gap={8}>
                      {actions.map((action: ReconcileAction, idx: number) => (
                        <div
                          key={idx}
                          style={{
                            border: '1px solid #eaecf0',
                            borderRadius: 10,
                            padding: 12,
                            background: '#f8fafc',
                          }}
                        >
                          <Stack gap={8} align="center" wrap>
                            <Chip
                              color={
                                isExecuted
                                  ? 'success'
                                  : action.action === 'error'
                                    ? 'error'
                                    : 'warning'
                              }
                              label={actionLabel(action.action)}
                              size="small"
                            />
                            <Small>Invoice #{action.invoice_id}</Small>
                            {action.stripe_invoice_id && (
                              <Small>Stripe {action.stripe_invoice_id}</Small>
                            )}
                          </Stack>
                          <Stack vertical gap={4} css={{ marginTop: 8 }}>
                            {Object.keys(action.details || {}).length > 0 ? (
                              Object.entries(action.details).map(
                                ([key, value]) => (
                                  <Small key={key}>
                                    <strong>{humanizeLabel(key)}:</strong>{' '}
                                    {formatReconcileDetailValue(value)}
                                  </Small>
                                ),
                              )
                            ) : (
                              <Small>No extra details provided.</Small>
                            )}
                          </Stack>
                        </div>
                      ))}
                    </Stack>
                  </Card.Section>
                </Card>
              )}

              <Stack justify="end" gap={12}>
                <Button
                  a11yLabel="Close"
                  appearance="outline"
                  label={isExecuted ? 'Done' : 'Cancel'}
                  disabled={executing}
                  type="button"
                  onClick={onClose}
                />
                {hasActions && !isExecuted && (
                  <Button
                    a11yLabel="Execute reconciliation"
                    label="Execute reconciliation"
                    isLoading={executing}
                    type="button"
                    onClick={onExecute}
                  />
                )}
              </Stack>
            </Stack>
          )}
        </Card.Section>
      </Card>
    </Modal>
  );
};

type ReconcileState = {
  accountId: number;
  accountName: string;
} | null;

type DetailState = CycleAccountWorkspace | null;
type CycleAnalysisState = {
  cycleLabel: string;
} | null;

type GlobalCleanupState = boolean;

type AccountDetailModalProps = {
  isOpen: boolean;
  loading: boolean;
  error: string | null;
  account: CycleAccount | null;
  accountDetail: DeepAccountDetail | null;
  onClose: () => void;
};

const AccountDetailModal: React.FC<AccountDetailModalProps> = ({
  isOpen,
  loading,
  error,
  account,
  accountDetail,
  onClose,
}) => {
  if (!isOpen || !account) return null;

  const summaryEntries = Object.entries(accountDetail?.summary || {});
  const duplicateInvoices =
    accountDetail?.duplicate_local_invoices_by_stripe_invoice_id || [];
  const duplicateItems =
    accountDetail?.duplicate_local_invoice_items_by_stripe_invoice_item_id ||
    [];

  return (
    <Modal
      disableClickout
      size="lg"
      title={`Account details for ${account.account_name}`}
      onRequestClose={onClose}
    >
      <Card>
        <Card.Section>
          <Stack vertical gap={6}>
            <Body weight="semibold">
              Review invoice coverage for this account
            </Body>
            <Small>
              Customer {account.customer_name} (#{account.customer_id}) and
              account #{account.account_id}
            </Small>
          </Stack>
        </Card.Section>

        <Card.Section>
          {loading && <LoadingState text="Loading account details..." />}
          {!loading && error && (
            <div style={{ color: '#b42318', fontSize: 14 }}>{error}</div>
          )}
          {!loading && accountDetail && (
            <Stack vertical gap={16}>
              <Card>
                <Card.Section>
                  <Stack vertical gap={10}>
                    <Body weight="semibold">Summary</Body>
                    <Stack gap={8} wrap>
                      <Chip
                        color="default"
                        label={`${accountDetail.analyzed_timesheet_ids.length} timesheets analyzed`}
                        size="small"
                      />
                      <Chip
                        color="default"
                        label={`${accountDetail.invoices.length} related invoices`}
                        size="small"
                      />
                      <Chip
                        color={
                          duplicateInvoices.length > 0 ? 'warning' : 'success'
                        }
                        label={`${duplicateInvoices.length} duplicate invoices`}
                        size="small"
                      />
                      <Chip
                        color={
                          duplicateItems.length > 0 ? 'warning' : 'success'
                        }
                        label={`${duplicateItems.length} duplicate invoice items`}
                        size="small"
                      />
                    </Stack>
                    {summaryEntries.length > 0 && (
                      <Stack gap={8} wrap>
                        {summaryEntries.map(([status, count]) => (
                          <Chip
                            key={status}
                            color="default"
                            label={`${humanizeLabel(status)} ${count}`}
                            size="small"
                          />
                        ))}
                      </Stack>
                    )}
                  </Stack>
                </Card.Section>
              </Card>

              <Card>
                <Card.Section>
                  <Body weight="semibold">
                    Timesheets ({accountDetail.timesheets.length})
                  </Body>
                </Card.Section>
                <Card.Section>
                  <Stack vertical gap={8}>
                    {accountDetail.timesheets.length === 0 ? (
                      <Small>No timesheets returned for this account.</Small>
                    ) : (
                      accountDetail.timesheets.map((timesheetDetail) => (
                        <div
                          key={timesheetDetail.timesheet.id}
                          style={{
                            border: '1px solid #eaecf0',
                            borderRadius: 10,
                            padding: 12,
                            background: '#f8fafc',
                          }}
                        >
                          <Stack gap={8} align="center" wrap>
                            <Body weight="semibold">
                              Timesheet {timesheetDetail.timesheet.id}
                            </Body>
                            <Chip
                              color={statusColor(timesheetDetail.status)}
                              label={humanizeLabel(timesheetDetail.status)}
                              size="small"
                            />
                            <Chip
                              color="default"
                              label={humanizeLabel(
                                timesheetDetail.recommended_action,
                              )}
                              size="small"
                            />
                          </Stack>
                          <Stack vertical gap={4} css={{ marginTop: 8 }}>
                            <Small>
                              Active invoice items:{' '}
                              {timesheetDetail.active_invoice_items.length}
                            </Small>
                            {timesheetDetail.net_time_analysis && (
                              <Small>
                                Net time:{' '}
                                {
                                  timesheetDetail.net_time_analysis
                                    .expected_net_time
                                }
                                min expected,{' '}
                                {
                                  timesheetDetail.net_time_analysis
                                    .actual_net_time
                                }
                                min actual
                              </Small>
                            )}
                            {timesheetDetail.issues.length > 0 && (
                              <Stack gap={8} wrap>
                                {timesheetDetail.issues.map((issue) => (
                                  <Chip
                                    key={issue}
                                    color="warning"
                                    label={humanizeLabel(issue)}
                                    size="small"
                                  />
                                ))}
                              </Stack>
                            )}
                          </Stack>
                        </div>
                      ))
                    )}
                  </Stack>
                </Card.Section>
              </Card>

              <Card>
                <Card.Section>
                  <Body weight="semibold">
                    Related invoices ({accountDetail.invoices.length})
                  </Body>
                </Card.Section>
                <Card.Section>
                  <Stack vertical gap={8}>
                    {accountDetail.invoices.length === 0 ? (
                      <Small>No related invoices found.</Small>
                    ) : (
                      accountDetail.invoices.map((invoice) => (
                        <div
                          key={invoice.invoice.id}
                          style={{
                            border: '1px solid #eaecf0',
                            borderRadius: 10,
                            padding: 12,
                            background: '#f8fafc',
                          }}
                        >
                          <Stack gap={8} align="center" wrap>
                            <Body weight="semibold">
                              Invoice #{invoice.invoice.id}
                            </Body>
                            <Chip
                              color="default"
                              label={humanizeLabel(invoice.status)}
                              size="small"
                            />
                            {invoice.invoice.stripe_invoice_id && (
                              <Small>
                                Stripe {invoice.invoice.stripe_invoice_id}
                              </Small>
                            )}
                          </Stack>
                          <Stack vertical gap={4} css={{ marginTop: 8 }}>
                            <Small>
                              Local items: {invoice.local_invoice_items.length}
                            </Small>
                            <Small>
                              Stripe items:{' '}
                              {invoice.stripe_invoice_items.length}
                            </Small>
                            {invoice.issues.length > 0 && (
                              <Stack gap={8} wrap>
                                {invoice.issues.map((issue) => (
                                  <Chip
                                    key={issue}
                                    color="warning"
                                    label={humanizeLabel(issue)}
                                    size="small"
                                  />
                                ))}
                              </Stack>
                            )}
                          </Stack>
                        </div>
                      ))
                    )}
                  </Stack>
                </Card.Section>
              </Card>

              {(duplicateInvoices.length > 0 || duplicateItems.length > 0) && (
                <Card>
                  <Card.Section>
                    <Body weight="semibold">Duplicates</Body>
                  </Card.Section>
                  <Card.Section>
                    <Stack vertical gap={8}>
                      {duplicateInvoices.map((duplicate) => (
                        <Small key={duplicate.stripe_invoice_id}>
                          Stripe invoice {duplicate.stripe_invoice_id}: local
                          invoices {duplicate.invoice_ids.join(', ')}
                        </Small>
                      ))}
                      {duplicateItems.map((duplicate) => (
                        <Small key={duplicate.stripe_invoice_item_id}>
                          Stripe invoice item {duplicate.stripe_invoice_item_id}
                          : local items {duplicate.invoice_item_ids.join(', ')}
                        </Small>
                      ))}
                    </Stack>
                  </Card.Section>
                </Card>
              )}

              <Stack justify="end" gap={12}>
                <Button
                  a11yLabel="Close account details"
                  appearance="outline"
                  label="Close"
                  type="button"
                  onClick={onClose}
                />
              </Stack>
            </Stack>
          )}
        </Card.Section>
      </Card>
    </Modal>
  );
};

type CycleAnalysisModalProps = {
  isOpen: boolean;
  cycleLabel: string | null;
  loading: boolean;
  error: string | null;
  analysis: CycleAnalysisResult | null;
  onClose: () => void;
};

const CycleAnalysisModal: React.FC<CycleAnalysisModalProps> = ({
  isOpen,
  cycleLabel,
  loading,
  error,
  analysis,
  onClose,
}) => {
  if (!isOpen) return null;

  const summary = analysis?.summary;
  const problems = analysis?.problematic_timesheets || [];

  return (
    <Modal
      disableClickout
      size="lg"
      title={`Invoice analysis${cycleLabel ? ` for ${cycleLabel}` : ''}`}
      onRequestClose={onClose}
    >
      <Card>
        <Card.Section>
          <Stack vertical gap={6}>
            <Body weight="semibold">
              Review cycle completeness across all approved timesheets
            </Body>
            <Small>
              This checks whether approved cycle timesheets are attached to a
              valid non-void, non-deleted invoice and flags uninvoiced or stuck
              rows.
            </Small>
          </Stack>
        </Card.Section>

        <Card.Section>
          {loading && <LoadingState text="Running invoice analysis..." />}
          {!loading && error && (
            <div style={{ color: '#b42318', fontSize: 14 }}>{error}</div>
          )}
          {!loading && analysis && (
            <Stack vertical gap={16}>
              <Card>
                <Card.Section>
                  <Stack vertical gap={10}>
                    <Body weight="semibold">Summary</Body>
                    <Stack gap={8} wrap>
                      <Chip
                        color={summary.all_complete ? 'success' : 'warning'}
                        label={
                          summary.all_complete
                            ? 'All timesheets complete'
                            : `${summary.problematic_timesheets_count} problematic timesheets`
                        }
                        size="small"
                      />
                      <Chip
                        color="default"
                        label={`${summary.accounts_count} accounts`}
                        size="small"
                      />
                      <Chip
                        color="default"
                        label={`${summary.timesheets_count} timesheets`}
                        size="small"
                      />
                      <Chip
                        color="success"
                        label={`${summary.complete_timesheets_count} complete`}
                        size="small"
                      />
                      <Chip
                        color="warning"
                        label={`${summary.uninvoiced_timesheets_count} uninvoiced`}
                        size="small"
                      />
                      <Chip
                        color="error"
                        label={`${summary.stuck_timesheets_count} stuck`}
                        size="small"
                      />
                    </Stack>
                    {Object.entries(analysis.stats).length > 0 && (
                      <Stack gap={8} wrap>
                        {Object.entries(analysis.stats).map(([key, value]) => (
                          <Chip
                            key={key}
                            color="default"
                            label={`${humanizeLabel(key)} ${value}`}
                            size="small"
                          />
                        ))}
                      </Stack>
                    )}
                  </Stack>
                </Card.Section>
              </Card>

              {problems.length === 0 ? (
                <Card>
                  <Card.Section>
                    <EmptyState title="No uninvoiced or stuck timesheets found" />
                  </Card.Section>
                </Card>
              ) : (
                <Card>
                  <Card.Section>
                    <Body weight="semibold">
                      Problematic timesheets ({problems.length})
                    </Body>
                  </Card.Section>
                  <Card.Section>
                    <Stack vertical gap={8}>
                      {problems.map((problem) => {
                        const invoiceUrl = problem.linked_invoice_id
                          ? `/${ROUTES.invoices}/${problem.linked_invoice_id}`
                          : null;

                        return (
                          <div
                            key={problem.id}
                            style={{
                              border: '1px solid #eaecf0',
                              borderRadius: 10,
                              padding: 12,
                              background: '#f8fafc',
                            }}
                          >
                            <Stack gap={8} align="center" wrap>
                              <Body weight="semibold">
                                Timesheet {problem.id}
                              </Body>
                              <Chip
                                color={statusColor(problem.status)}
                                label={humanizeStatus(problem.status)}
                                size="small"
                              />
                              {problem.problem_labels.map((label) => (
                                <Chip
                                  key={`${problem.id}-${label}`}
                                  color="warning"
                                  label={label}
                                  size="small"
                                />
                              ))}
                            </Stack>
                            <Stack vertical gap={4} css={{ marginTop: 8 }}>
                              <Small>
                                {problem.customer_name} - {problem.account_name}
                              </Small>
                              <Small>
                                Worker {problem.worker_name || 'n/a'} • Group{' '}
                                {problem.derived_group_label}
                              </Small>
                              <Small>
                                Approved {formatDateTime(problem.approved_at)} •
                                First shift{' '}
                                {formatDateTime(problem.first_shift_starts_at)}
                              </Small>
                              <Small>
                                Amount{' '}
                                {formatCurrency(problem.total_amount_cents)}
                              </Small>
                              {problem.hold_by_invoice_id && (
                                <Small>
                                  Held by invoice #{problem.hold_by_invoice_id}
                                </Small>
                              )}
                              {invoiceUrl ? (
                                <Small>
                                  Linked invoice{' '}
                                  <Link css={linkedTextCss} to={invoiceUrl}>
                                    {problem.linked_invoice_number ||
                                      `#${problem.linked_invoice_id}`}
                                  </Link>
                                  {problem.linked_invoice_status
                                    ? ` • ${humanizeStatus(problem.linked_invoice_status)}`
                                    : ''}
                                </Small>
                              ) : (
                                <Small>No linked invoice</Small>
                              )}
                            </Stack>
                          </div>
                        );
                      })}
                    </Stack>
                  </Card.Section>
                </Card>
              )}

              <Stack justify="end" gap={12}>
                <Button
                  a11yLabel="Close invoice analysis"
                  appearance="outline"
                  label="Close"
                  type="button"
                  onClick={onClose}
                />
              </Stack>
            </Stack>
          )}
        </Card.Section>
      </Card>
    </Modal>
  );
};

type StuckHeldTimesheetsModalProps = {
  isOpen: boolean;
  loading: boolean;
  error: string | null;
  report: StuckHeldTimesheetsResult | null;
  loadingTimesheetId: number | null;
  onClose: () => void;
  onResolve: (
    timesheet: StuckHeldTimesheet,
    action: 'clear_hold' | 'restore_line_item',
  ) => void;
};

const StuckHeldTimesheetsModal: React.FC<StuckHeldTimesheetsModalProps> = ({
  isOpen,
  loading,
  error,
  report,
  loadingTimesheetId,
  onClose,
  onResolve,
}) => {
  if (!isOpen) return null;

  const summary = report?.summary;
  const timesheets = report?.timesheets || [];

  return (
    <Modal
      disableClickout
      size="lg"
      title="Stuck held timesheets"
      onRequestClose={onClose}
    >
      <Card>
        <Card.Section>
          <Stack vertical gap={6}>
            <Body weight="semibold">
              Review held timesheets that are missing a line item on their held
              invoice
            </Body>
            <Small>
              This runs globally and is not limited to the selected cycle.
            </Small>
          </Stack>
        </Card.Section>

        <Card.Section>
          {loading && <LoadingState text="Loading stuck held timesheets..." />}
          {!loading && error && (
            <div style={{ color: '#b42318', fontSize: 14 }}>{error}</div>
          )}
          {!loading && report && (
            <Stack vertical gap={16}>
              <Card>
                <Card.Section>
                  <Stack vertical gap={10}>
                    <Body weight="semibold">Summary</Body>
                    <Stack gap={8} wrap>
                      <Chip
                        color="default"
                        label={`${summary.timesheets_count} timesheets`}
                        size="small"
                      />
                      <Chip
                        color="success"
                        label={`${summary.actionable_timesheets_count} actionable`}
                        size="small"
                      />
                      <Chip
                        color="warning"
                        label={`${summary.clear_hold_count} clear hold`}
                        size="small"
                      />
                      <Chip
                        color="success"
                        label={`${summary.restore_line_item_count} restore line item`}
                        size="small"
                      />
                      <Chip
                        color="error"
                        label={`${summary.manual_review_count} manual review`}
                        size="small"
                      />
                    </Stack>
                    {Object.entries(report.stats).length > 0 && (
                      <Stack gap={8} wrap>
                        {Object.entries(report.stats).map(([key, value]) => (
                          <Chip
                            key={key}
                            color="default"
                            label={`${humanizeLabel(key)} ${value}`}
                            size="small"
                          />
                        ))}
                      </Stack>
                    )}
                  </Stack>
                </Card.Section>
              </Card>

              {timesheets.length === 0 ? (
                <Card>
                  <Card.Section>
                    <EmptyState title="No stuck held timesheets found" />
                  </Card.Section>
                </Card>
              ) : (
                <Card>
                  <Card.Section>
                    <Body weight="semibold">
                      Stuck held timesheets ({timesheets.length})
                    </Body>
                  </Card.Section>
                  <Card.Section>
                    <Stack vertical gap={8}>
                      {timesheets.map((timesheet) => {
                        const heldInvoiceUrl = timesheet.held_invoice_id
                          ? `/${ROUTES.invoices}/${timesheet.held_invoice_id}`
                          : null;
                        const conflictingInvoiceText =
                          timesheet.conflicting_invoice_ids.length > 0
                            ? `Conflicting invoices ${timesheet.conflicting_invoice_ids.join(', ')}`
                            : null;

                        return (
                          <div
                            key={timesheet.id}
                            style={{
                              border: '1px solid #eaecf0',
                              borderRadius: 10,
                              padding: 12,
                              background: '#f8fafc',
                            }}
                          >
                            <Stack justify="apart" align="start" gap={16}>
                              <Stack vertical gap={4} css={{ flex: 1 }}>
                                <Stack gap={8} align="center" wrap>
                                  <Body weight="semibold">
                                    Timesheet {timesheet.id}
                                  </Body>
                                  <Chip
                                    color={
                                      timesheet.allowed_actions.length > 0
                                        ? 'warning'
                                        : 'error'
                                    }
                                    label={timesheet.problem_label}
                                    size="small"
                                  />
                                </Stack>
                                <Small>
                                  {timesheet.customer_name} -{' '}
                                  {timesheet.account_name}
                                </Small>
                                <Small>
                                  Worker {timesheet.worker_name || 'n/a'} •
                                  Approved{' '}
                                  {formatDateTime(timesheet.approved_at)}
                                </Small>
                                <Small>
                                  First shift{' '}
                                  {formatDateTime(
                                    timesheet.first_shift_starts_at,
                                  )}
                                </Small>
                                <Small>
                                  Held invoice{' '}
                                  {heldInvoiceUrl ? (
                                    <Link
                                      css={linkedTextCss}
                                      to={heldInvoiceUrl}
                                    >
                                      {timesheet.held_invoice_number ||
                                        `#${timesheet.held_invoice_id}`}
                                    </Link>
                                  ) : (
                                    `#${timesheet.held_invoice_id}`
                                  )}
                                  {timesheet.held_invoice_status
                                    ? ` • ${humanizeStatus(timesheet.held_invoice_status)}`
                                    : ''}
                                </Small>
                                {timesheet.stripe_invoice_item_id && (
                                  <Small>
                                    Stale Stripe invoice item{' '}
                                    {timesheet.stripe_invoice_item_id}
                                  </Small>
                                )}
                                {conflictingInvoiceText && (
                                  <Small style={{ color: '#b42318' }}>
                                    {conflictingInvoiceText}
                                  </Small>
                                )}
                              </Stack>
                              <Stack gap={8} wrap>
                                {timesheet.allowed_actions.includes(
                                  'clear_hold',
                                ) && (
                                  <Button
                                    a11yLabel={`Clear hold for timesheet ${timesheet.id}`}
                                    appearance="outline"
                                    label="Clear hold"
                                    isLoading={
                                      loadingTimesheetId === timesheet.id
                                    }
                                    type="button"
                                    onClick={() =>
                                      onResolve(timesheet, 'clear_hold')
                                    }
                                  />
                                )}
                                {timesheet.allowed_actions.includes(
                                  'restore_line_item',
                                ) && (
                                  <Button
                                    a11yLabel={`Restore line item for timesheet ${timesheet.id}`}
                                    label="Restore line item"
                                    isLoading={
                                      loadingTimesheetId === timesheet.id
                                    }
                                    type="button"
                                    onClick={() =>
                                      onResolve(timesheet, 'restore_line_item')
                                    }
                                  />
                                )}
                                {timesheet.allowed_actions.length === 0 && (
                                  <Small style={{ color: '#b42318' }}>
                                    Manual review required
                                  </Small>
                                )}
                              </Stack>
                            </Stack>
                          </div>
                        );
                      })}
                    </Stack>
                  </Card.Section>
                </Card>
              )}

              <Stack justify="end" gap={12}>
                <Button
                  a11yLabel="Close stuck held timesheets"
                  appearance="outline"
                  label="Close"
                  type="button"
                  onClick={onClose}
                />
              </Stack>
            </Stack>
          )}
        </Card.Section>
      </Card>
    </Modal>
  );
};

type EmptyDraftInvoicesModalProps = {
  isOpen: boolean;
  loading: boolean;
  error: string | null;
  report: EmptyDraftInvoicesResult | null;
  loadingInvoiceId: number | null;
  onClose: () => void;
  onDelete: (invoice: EmptyDraftInvoice) => void;
};

const EmptyDraftInvoicesModal: React.FC<EmptyDraftInvoicesModalProps> = ({
  isOpen,
  loading,
  error,
  report,
  loadingInvoiceId,
  onClose,
  onDelete,
}) => {
  if (!isOpen) return null;

  const summary = report?.summary;
  const invoices = report?.invoices || [];

  return (
    <Modal
      disableClickout
      size="lg"
      title="Empty draft invoices"
      onRequestClose={onClose}
    >
      <Card>
        <Card.Section>
          <Stack vertical gap={6}>
            <Body weight="semibold">
              Review draft invoices that have no active line items
            </Body>
            <Small>
              This runs globally and is not limited to the selected cycle.
            </Small>
          </Stack>
        </Card.Section>

        <Card.Section>
          {loading && <LoadingState text="Loading empty draft invoices..." />}
          {!loading && error && (
            <div style={{ color: '#b42318', fontSize: 14 }}>{error}</div>
          )}
          {!loading && report && (
            <Stack vertical gap={16}>
              <Card>
                <Card.Section>
                  <Stack vertical gap={10}>
                    <Body weight="semibold">Summary</Body>
                    <Stack gap={8} wrap>
                      <Chip
                        color="default"
                        label={`${summary.invoices_count} invoices`}
                        size="small"
                      />
                      <Chip
                        color="success"
                        label={`${summary.soft_delete_count} safe to delete`}
                        size="small"
                      />
                      <Chip
                        color="error"
                        label={`${summary.manual_review_count} manual review`}
                        size="small"
                      />
                    </Stack>
                    {Object.entries(report.stats).length > 0 && (
                      <Stack gap={8} wrap>
                        {Object.entries(report.stats).map(([key, value]) => (
                          <Chip
                            key={key}
                            color="default"
                            label={`${humanizeLabel(key)} ${value}`}
                            size="small"
                          />
                        ))}
                      </Stack>
                    )}
                  </Stack>
                </Card.Section>
              </Card>

              {invoices.length === 0 ? (
                <Card>
                  <Card.Section>
                    <EmptyState title="No empty draft invoices found" />
                  </Card.Section>
                </Card>
              ) : (
                <Card>
                  <Card.Section>
                    <Body weight="semibold">
                      Empty draft invoices ({invoices.length})
                    </Body>
                  </Card.Section>
                  <Card.Section>
                    <Stack vertical gap={8}>
                      {invoices.map((invoice) => {
                        const invoiceUrl = `/${ROUTES.invoices}/${invoice.id}`;
                        const duplicateText =
                          invoice.duplicate_bucket_invoice_ids.length > 0
                            ? `Other invoices in bucket ${invoice.duplicate_bucket_invoice_ids.join(', ')}`
                            : null;

                        return (
                          <div
                            key={invoice.id}
                            style={{
                              border: '1px solid #eaecf0',
                              borderRadius: 10,
                              padding: 12,
                              background: '#f8fafc',
                            }}
                          >
                            <Stack justify="apart" align="start" gap={16}>
                              <Stack vertical gap={4} css={{ flex: 1 }}>
                                <Stack gap={8} align="center" wrap>
                                  <Body weight="semibold">
                                    <Link css={linkedTextCss} to={invoiceUrl}>
                                      Invoice #{invoice.id}
                                    </Link>
                                  </Body>
                                  <Chip
                                    color={
                                      invoice.allowed_actions.length > 0
                                        ? 'success'
                                        : 'error'
                                    }
                                    label={invoice.problem_label}
                                    size="small"
                                  />
                                </Stack>
                                <Small>
                                  {invoice.customer_name} -{' '}
                                  {invoice.account_name}
                                </Small>
                                <Small>
                                  Status {humanizeStatus(invoice.status)} •
                                  Active line items{' '}
                                  {invoice.active_line_items_count}
                                </Small>
                                <Small>
                                  Total{' '}
                                  {formatCurrency(invoice.total_amount_cents)} •
                                  Amount due{' '}
                                  {formatCurrency(invoice.amount_due_cents)}
                                </Small>
                                <Small>
                                  Updated {formatDateTime(invoice.updated_at)}
                                </Small>
                                {duplicateText && (
                                  <Small style={{ color: '#b42318' }}>
                                    {duplicateText}
                                  </Small>
                                )}
                              </Stack>
                              <Stack gap={8} wrap>
                                {invoice.allowed_actions.includes(
                                  'soft_delete_invoice',
                                ) ? (
                                  <Button
                                    a11yLabel={`Soft delete invoice ${invoice.id}`}
                                    appearance="outline"
                                    label="Soft delete invoice"
                                    isLoading={loadingInvoiceId === invoice.id}
                                    type="button"
                                    onClick={() => onDelete(invoice)}
                                  />
                                ) : (
                                  <Small style={{ color: '#b42318' }}>
                                    Manual review required
                                  </Small>
                                )}
                              </Stack>
                            </Stack>
                          </div>
                        );
                      })}
                    </Stack>
                  </Card.Section>
                </Card>
              )}

              <Stack justify="end" gap={12}>
                <Button
                  a11yLabel="Close empty draft invoices"
                  appearance="outline"
                  label="Close"
                  type="button"
                  onClick={onClose}
                />
              </Stack>
            </Stack>
          )}
        </Card.Section>
      </Card>
    </Modal>
  );
};

type AccountListPanelProps = {
  accounts: CycleAccount[];
  selectedAccountId: number | null;
  totalAccountsCount: number;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  accountFilter: AccountFilter;
  onAccountFilterChange: (value: AccountFilter) => void;
  onSelect: (account: CycleAccount) => void;
};

const ACCOUNT_FILTER_OPTIONS: Array<{
  value: AccountFilter;
  label: string;
}> = [
  { value: 'all', label: 'All' },
  { value: 'incomplete', label: 'Needs action' },
  { value: 'no_invoice', label: 'No invoice' },
  { value: 'complete', label: 'Complete' },
];

const AccountListPanel: React.FC<AccountListPanelProps> = ({
  accounts,
  selectedAccountId,
  totalAccountsCount,
  searchQuery,
  onSearchChange,
  accountFilter,
  onAccountFilterChange,
  onSelect,
}) => {
  return (
    <Card
      css={{
        position: 'sticky',
        top: 16,
      }}
    >
      <Card.Section>
        <Stack vertical gap={8}>
          <Small>
            {accounts.length} of {totalAccountsCount} accounts
          </Small>

          <input
            value={searchQuery}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search account or customer"
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: 8,
              border: '1px solid #d0d5dd',
              background: '#fff',
              fontSize: 13,
              color: '#344054',
            }}
          />

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
            }}
          >
            {ACCOUNT_FILTER_OPTIONS.map((option) => {
              const selected = accountFilter === option.value;

              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onAccountFilterChange(option.value)}
                  style={{
                    borderRadius: 999,
                    border: selected
                      ? '1px solid #16a34a'
                      : '1px solid #d0d5dd',
                    background: selected ? '#f0fdf4' : '#fff',
                    color: selected ? '#166534' : '#475467',
                    fontSize: 12,
                    fontWeight: 600,
                    padding: '6px 10px',
                    cursor: 'pointer',
                  }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </Stack>
      </Card.Section>
      <Card.Section>
        {accounts.length === 0 ? (
          <EmptyState title="No accounts match that search" />
        ) : (
          <div
            style={{
              maxHeight: 'calc(100vh - 280px)',
              minHeight: 360,
              overflowY: 'auto',
              paddingRight: 4,
            }}
          >
            <Stack vertical gap={8}>
              {accounts.map((account) => {
                const status = accountStatusMeta(account);
                const isSelected = selectedAccountId === account.account_id;

                return (
                  <button
                    key={account.account_id}
                    aria-label={`Select account ${account.account_id}`}
                    type="button"
                    onClick={() => onSelect(account)}
                    style={{
                      width: '100%',
                      borderRadius: 10,
                      border: isSelected
                        ? '2px solid #16a34a'
                        : '1px solid #e4e7ec',
                      background: isSelected ? '#f0fdf4' : '#fff',
                      padding: '10px 12px',
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    <Stack justify="apart" align="center" gap={8}>
                      <Stack vertical gap={2} css={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 14,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={account.account_name}
                        >
                          {account.account_name}
                        </div>
                        <Small>
                          #{account.account_id} • {account.customer_name}
                        </Small>
                      </Stack>
                      <Stack
                        vertical
                        gap={4}
                        align="end"
                        css={{ flexShrink: 0 }}
                      >
                        <Chip
                          color={status.color}
                          label={status.label}
                          size="small"
                        />
                        <Small>{account.timesheets_count} ts</Small>
                      </Stack>
                    </Stack>
                  </button>
                );
              })}
            </Stack>
          </div>
        )}
      </Card.Section>
    </Card>
  );
};

type AccountCardProps = {
  account: CycleAccountWorkspace;
  loadingAssignInvoiceId: number | null;
  loadingRemoveItemId: number | null;
  loadingCreateAccountId: number | null;
  loadingVoidInvoiceId: number | null;
  onAssignTimesheets: (
    account: CycleAccountWorkspace,
    invoiceId: number,
    timesheetIds: number[],
  ) => Promise<boolean>;
  onRemoveLineItem: (
    account: CycleAccountWorkspace,
    timesheet: CycleTimesheet,
  ) => void;
  onInspectInvoice: (account: CycleAccountWorkspace) => void;
  onCreateInvoice: (account: CycleAccountWorkspace) => void;
  onVoidInvoice: (
    account: CycleAccountWorkspace,
    invoice: CycleInvoiceSection,
  ) => void;
  onReconcile: (account: CycleAccountWorkspace) => void;
};

const AccountCard: React.FC<AccountCardProps> = ({
  account,
  loadingAssignInvoiceId,
  loadingRemoveItemId,
  loadingCreateAccountId,
  loadingVoidInvoiceId,
  onAssignTimesheets,
  onRemoveLineItem,
  onInspectInvoice,
  onCreateInvoice,
  onVoidInvoice,
  onReconcile,
}) => {
  const [selectedTimesheetIds, setSelectedTimesheetIds] = useState<number[]>(
    [],
  );
  const [targetInvoiceId, setTargetInvoiceId] = useState<number | null>(null);
  const conflictedGroups = account.conflicted_groups || [];
  const accountStatus = accountStatusMeta(account);

  const selectedTimesheets = useMemo(
    () =>
      account.unassigned_timesheets.filter((timesheet) =>
        selectedTimesheetIds.includes(timesheet.id),
      ),
    [account.unassigned_timesheets, selectedTimesheetIds],
  );

  const compatibleInvoices = useMemo(() => {
    if (selectedTimesheets.length === 0) return account.invoices;

    return account.invoices.filter((invoice) =>
      selectedTimesheets.every((timesheet) =>
        timesheet.assignable_invoice_ids.includes(invoice.id),
      ),
    );
  }, [account.invoices, selectedTimesheets]);

  useEffect(() => {
    if (compatibleInvoices.length === 0) {
      setTargetInvoiceId(null);
      return;
    }

    if (
      targetInvoiceId != null &&
      compatibleInvoices.some((invoice) => invoice.id === targetInvoiceId)
    ) {
      return;
    }

    setTargetInvoiceId(compatibleInvoices[0].id);
  }, [compatibleInvoices, targetInvoiceId]);

  useEffect(() => {
    setSelectedTimesheetIds([]);
    setTargetInvoiceId(null);
  }, [account.account_id, account.invoices, account.unassigned_timesheets]);

  const toggleTimesheet = (timesheetId: number) => {
    setSelectedTimesheetIds((current) =>
      current.includes(timesheetId)
        ? current.filter((value) => value !== timesheetId)
        : [...current, timesheetId],
    );
  };

  const renderTimesheetTable = (
    timesheets: CycleTimesheet[],
    options: { invoice?: CycleInvoiceSection; showSelection?: boolean } = {},
  ) => {
    const { invoice, showSelection = false } = options;

    return (
      <div style={{ overflowX: 'auto' }}>
        <Table>
          <TableHeader
            fields={[
              ...(showSelection ? [{ name: '', style: { width: 56 } }] : []),
              { name: 'TIMESHEET', style: { width: 90 } },
              { name: 'WORKER', style: { width: 180 } },
              { name: 'GROUP', style: { width: 220 } },
              { name: 'FIRST SHIFT STARTS', style: { width: 210 } },
              { name: 'STATUS', style: { width: 180 } },
              { name: 'APPROVED AT', style: { width: 210 } },
              { name: 'TOTAL AMOUNT', style: { width: 140 } },
              { name: 'LINE ITEM', style: { width: 220 } },
              { name: 'INVOICE', style: { width: 170 } },
              { name: '', style: { width: 160 } },
            ]}
          />
          <tbody>
            {timesheets.map((timesheet) => {
              const timesheetUrl =
                timesheet.order_id != null && timesheet.job_id != null
                  ? `/${ROUTES.orders}/${timesheet.order_id}/jobs/${timesheet.job_id}`
                  : null;
              const linkedInvoiceUrl = timesheet.linked_invoice_id
                ? `/${ROUTES.invoices}/${timesheet.linked_invoice_id}`
                : null;
              const canSelect =
                showSelection &&
                timesheet.status === 'not_included' &&
                timesheet.assignable_invoice_ids.length > 0;
              const isSelected = selectedTimesheetIds.includes(timesheet.id);

              return (
                <TableRow key={timesheet.id}>
                  {showSelection && (
                    <TableCell>
                      <input
                        checked={isSelected}
                        disabled={!canSelect}
                        type="checkbox"
                        onChange={() => toggleTimesheet(timesheet.id)}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    {timesheetUrl ? (
                      <Link css={linkedTextCss} to={timesheetUrl}>
                        <Body weight="semibold">{timesheet.id}</Body>
                      </Link>
                    ) : (
                      <Body css={linkedTextCss} weight="semibold">
                        {timesheet.id}
                      </Body>
                    )}
                  </TableCell>
                  <TableCell>
                    {timesheet.worker_id && timesheet.worker_name ? (
                      <Link
                        css={linkedTextCss}
                        to={`/${ROUTES.workersV2}/${timesheet.worker_id}`}
                      >
                        <Small>{timesheet.worker_name}</Small>
                      </Link>
                    ) : (
                      <Small>{timesheet.worker_name || 'n/a'}</Small>
                    )}
                  </TableCell>
                  <TableCell>
                    <Small>{timesheet.derived_group_label}</Small>
                  </TableCell>
                  <TableCell>
                    <Small>
                      {formatDateTime(timesheet.first_shift_starts_at)}
                    </Small>
                  </TableCell>
                  <TableCell>
                    <Chip
                      color={statusColor(timesheet.status)}
                      label={humanizeStatus(timesheet.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Small>{formatDateTime(timesheet.approved_at)}</Small>
                  </TableCell>
                  <TableCell>
                    <Small>
                      {formatCurrency(timesheet.total_amount_cents)}
                    </Small>
                  </TableCell>
                  <TableCell>
                    {timesheet.invoice_line_item_id ? (
                      <Small>Line item #{timesheet.invoice_line_item_id}</Small>
                    ) : (
                      <Small>No line item</Small>
                    )}
                  </TableCell>
                  <TableCell>
                    {linkedInvoiceUrl ? (
                      <Link css={linkedTextCss} to={linkedInvoiceUrl}>
                        <Small>
                          {timesheet.linked_invoice_number ||
                            `#${timesheet.linked_invoice_id}`}
                        </Small>
                      </Link>
                    ) : invoice ? (
                      <Small>Invoice #{invoice.id}</Small>
                    ) : (
                      <Small>Not assigned</Small>
                    )}
                  </TableCell>
                  <TableCell>
                    {invoice && timesheet.invoice_line_item_id ? (
                      <Button
                        a11yLabel={`Remove line item for timesheet ${timesheet.id}`}
                        appearance="outline"
                        label="Remove"
                        isLoading={
                          loadingRemoveItemId === timesheet.invoice_line_item_id
                        }
                        type="button"
                        onClick={() => onRemoveLineItem(account, timesheet)}
                      />
                    ) : (
                      <Small>
                        {timesheet.status === 'not_included'
                          ? 'Ready to assign'
                          : humanizeStatus(timesheet.status)}
                      </Small>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  };

  return (
    <Card>
      <Card.Section>
        <Stack vertical gap={14}>
          <Stack justify="apart" align="start" gap={16}>
            <Stack vertical gap={6} css={{ flex: 1, minWidth: 0 }}>
              <Stack gap={8} align="center" wrap>
                <Heading>
                  {account.customer_name} - {account.account_name}
                </Heading>
                <Chip
                  color={accountStatus.color}
                  label={accountStatus.label}
                  size="small"
                />
                <Chip
                  color="default"
                  label={`${account.timesheets_count} timesheets`}
                  size="small"
                />
                <Chip
                  color="default"
                  label={`${account.invoices.length} invoices`}
                  size="small"
                />
                {conflictedGroups.length > 0 && (
                  <Chip
                    color="error"
                    label={`${conflictedGroups.length} conflicted groups`}
                    size="small"
                  />
                )}
              </Stack>
              <Small>
                {account.invoices.length > 0
                  ? `${account.invoices.length} linked invoices • ${account.missing_timesheets_count} unassigned`
                  : `${account.missing_timesheets_count} unassigned timesheets`}
              </Small>
            </Stack>
          </Stack>

          <Stack gap={10} wrap>
            <div
              style={{
                padding: '8px 12px',
                borderRadius: 10,
                background: '#f8fafc',
                border: '1px solid #e4e7ec',
                minWidth: 180,
              }}
            >
              <Small>Customer</Small>
              <Body weight="semibold">{account.customer_name}</Body>
              <Small>ID {account.customer_id}</Small>
            </div>
            <div
              style={{
                padding: '8px 12px',
                borderRadius: 10,
                background: '#f8fafc',
                border: '1px solid #e4e7ec',
                minWidth: 160,
              }}
            >
              <Small>Account</Small>
              <Body weight="semibold">{account.account_name}</Body>
              <Small>ID {account.account_id}</Small>
            </div>
            <div
              style={{
                padding: '8px 12px',
                borderRadius: 10,
                background: '#f8fafc',
                border: '1px solid #e4e7ec',
                minWidth: 160,
              }}
            >
              <Small>Ready To Assign</Small>
              <Body weight="semibold">{actionableMissingCount(account)}</Body>
              <Small>Eligible unassigned timesheets</Small>
            </div>
            <div
              style={{
                padding: '8px 12px',
                borderRadius: 10,
                background: '#f8fafc',
                border: '1px solid #e4e7ec',
                minWidth: 160,
              }}
            >
              <Small>Conflicted Groups</Small>
              <Body weight="semibold">{conflictedGroups.length}</Body>
              <Small>Requires review before assignment</Small>
            </div>
          </Stack>
        </Stack>
      </Card.Section>

      <Card.Section>
        <Stack gap={8} wrap>
          <Button
            a11yLabel={`Create invoice for account ${account.account_id}`}
            appearance="secondary"
            label="Create invoice"
            isLoading={loadingCreateAccountId === account.account_id}
            type="button"
            onClick={() => onCreateInvoice(account)}
          />
          {account.invoices.length > 0 && (
            <Button
              a11yLabel={`Reconcile invoice for account ${account.account_id}`}
              appearance="secondary"
              label="Reconcile"
              type="button"
              onClick={() => onReconcile(account)}
            />
          )}
          <Button
            a11yLabel={`Explore invoice details for account ${account.account_id}`}
            appearance="secondary"
            label="Explore more details"
            type="button"
            onClick={() => onInspectInvoice(account)}
          />
        </Stack>
      </Card.Section>

      {conflictedGroups.length > 0 && (
        <Card.Section>
          <Stack vertical gap={6}>
            <Body weight="semibold">Conflicted groups</Body>
            {conflictedGroups.map((group) => (
              <Small key={group.group_key} style={{ color: '#b42318' }}>
                {group.group_label}: invoices {group.invoice_ids.join(', ')}
              </Small>
            ))}
          </Stack>
        </Card.Section>
      )}

      <Card.Section>
        <Stack vertical gap={16}>
          {account.invoices.map((invoice) => {
            const invoiceUrl = `/${ROUTES.invoices}/${invoice.id}`;
            const stripeReference =
              invoice.number || invoice.stripe_invoice_id || null;
            const canVoid =
              invoice.status === 'open' && !invoice.duplicate_in_group;

            return (
              <Card key={invoice.id}>
                <Card.Section>
                  <Stack justify="apart" align="start" gap={16}>
                    <Stack vertical gap={6}>
                      <Stack gap={8} align="center" wrap>
                        <Body weight="semibold">
                          <Link css={linkedTextCss} to={invoiceUrl}>
                            Invoice #{invoice.id}
                          </Link>
                        </Body>
                        <Chip
                          color="default"
                          label={invoice.group_label}
                          size="small"
                        />
                        <Chip
                          color="default"
                          label={invoice.status}
                          size="small"
                        />
                        {invoice.duplicate_in_group && (
                          <Chip
                            color="error"
                            label="Conflicted group"
                            size="small"
                          />
                        )}
                      </Stack>
                      <Small>
                        {invoice.timesheets_count} timesheets •{' '}
                        {formatCurrency(invoice.total_amount_cents)}
                      </Small>
                      {stripeReference && (
                        <Small>Stripe reference: {stripeReference}</Small>
                      )}
                    </Stack>
                    {canVoid && (
                      <Button
                        a11yLabel={`Void invoice ${invoice.id}`}
                        appearance="outline"
                        label="Void invoice"
                        isLoading={loadingVoidInvoiceId === invoice.id}
                        type="button"
                        onClick={() => onVoidInvoice(account, invoice)}
                      />
                    )}
                  </Stack>
                </Card.Section>
                <Card.Section>
                  {invoice.timesheets.length === 0 ? (
                    <Small>No timesheets are currently assigned.</Small>
                  ) : (
                    renderTimesheetTable(invoice.timesheets, { invoice })
                  )}
                </Card.Section>
              </Card>
            );
          })}

          <Card>
            <Card.Section>
              <Stack justify="apart" align="start" gap={16}>
                <Stack vertical gap={6}>
                  <Body weight="semibold">Unassigned Timesheets</Body>
                  <Small>
                    {account.unassigned_timesheets.length} timesheets not
                    currently attached to a cycle invoice
                  </Small>
                </Stack>
                {selectedTimesheetIds.length > 0 && (
                  <Stack gap={8} align="center" wrap>
                    <Small>{selectedTimesheetIds.length} selected</Small>
                    <select
                      value={targetInvoiceId ?? ''}
                      onChange={(event) =>
                        setTargetInvoiceId(Number(event.target.value))
                      }
                      style={{
                        minWidth: 220,
                        padding: '10px 14px',
                        borderRadius: 8,
                        border: '1px solid #d0d5dd',
                        background: '#fff',
                        fontSize: 14,
                      }}
                    >
                      {compatibleInvoices.map((invoice) => (
                        <option key={invoice.id} value={invoice.id}>
                          Invoice #{invoice.id} • {invoice.group_label}
                        </option>
                      ))}
                    </select>
                    <Button
                      a11yLabel={`Assign selected timesheets for account ${account.account_id}`}
                      disabled={
                        targetInvoiceId == null ||
                        compatibleInvoices.length === 0
                      }
                      appearance="secondary"
                      label="Assign to invoice"
                      isLoading={loadingAssignInvoiceId === targetInvoiceId}
                      type="button"
                      onClick={() => {
                        if (targetInvoiceId == null) return;
                        void onAssignTimesheets(
                          account,
                          targetInvoiceId,
                          selectedTimesheetIds,
                        ).then((success) => {
                          if (success) {
                            setSelectedTimesheetIds([]);
                          }
                        });
                      }}
                    />
                  </Stack>
                )}
              </Stack>
            </Card.Section>
            <Card.Section>
              {account.unassigned_timesheets.length === 0 ? (
                <Small>No unassigned timesheets in this cycle.</Small>
              ) : (
                renderTimesheetTable(account.unassigned_timesheets, {
                  showSelection: true,
                })
              )}
            </Card.Section>
          </Card>
        </Stack>
      </Card.Section>
    </Card>
  );
};

const InvoiceHealth = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem(INVOICE_HEALTH_AUTH_KEY) === 'true';
  });
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [selectedCycleId, setSelectedCycleId] = useState<number | null>(() =>
    parseInteger(searchParams.get('cycle')),
  );
  const [activeTab, setActiveTab] = useState<WorkspaceTab>(() =>
    searchParams.get('tab') === 'diagnostics' ? 'diagnostics' : 'accounts',
  );
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(
    () => parseInteger(searchParams.get('account')),
  );
  const [searchQuery, setSearchQuery] = useState(
    () => searchParams.get('search') || '',
  );
  const [accountFilter, setAccountFilter] = useState<AccountFilter>(() => {
    const value = searchParams.get('filter');
    return value === 'complete' ||
      value === 'incomplete' ||
      value === 'no_invoice'
      ? value
      : 'all';
  });
  const [confirmCreateState, setConfirmCreateState] =
    useState<ConfirmCreateState>(null);
  const [cycleAnalysisState, setCycleAnalysisState] =
    useState<CycleAnalysisState>(null);
  const [isStuckHeldModalOpen, setIsStuckHeldModalOpen] =
    useState<GlobalCleanupState>(false);
  const [isEmptyDraftModalOpen, setIsEmptyDraftModalOpen] =
    useState<GlobalCleanupState>(false);
  const [reconcileState, setReconcileState] = useState<ReconcileState>(null);
  const [detailState, setDetailState] = useState<DetailState>(null);
  const cycles = useCycles();
  const cycleAccounts = useCycleAccounts();
  const cycleAnalysis = useCycleAnalysis();
  const stuckHeldTimesheets = useStuckHeldTimesheets();
  const resolveStuckHeldTimesheet = useResolveStuckHeldTimesheet();
  const emptyDraftInvoices = useEmptyDraftInvoices();
  const softDeleteEmptyDraftInvoice = useSoftDeleteEmptyDraftInvoice();
  const assignTimesheets = useAssignTimesheets();
  const accountWorkspace = useCycleAccountWorkspace();
  const removeLineItem = useRemoveLineItem();
  const createInvoice = useCreateInvoice();
  const voidInvoice = useVoidInvoice();
  const reconcile = useReconcile();
  const accountDetail = useAccountDetail();
  const [workspaceCache, setWorkspaceCache] = useState<
    Record<string, CycleAccountWorkspace>
  >({});
  const isPhone = useMediaQuery(BREAKPOINTS.phoneOnly);
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const selectedCycle = useMemo(
    () => cycles.cycles.find((cycle) => cycle.id === selectedCycleId) || null,
    [cycles.cycles, selectedCycleId],
  );
  const cycleParams = useMemo(
    () =>
      selectedCycle
        ? {
            from: selectedCycle.from,
            to: selectedCycle.to,
            referenceTime: selectedCycle.started_at,
            futurePreview: selectedCycle.is_preview,
          }
        : undefined,
    [selectedCycle],
  );

  const refreshAccounts = useCallback(() => {
    if (!selectedCycle) return;
    cycleAccounts.fetch({
      from: selectedCycle.from,
      to: selectedCycle.to,
      referenceTime: selectedCycle.started_at,
      futurePreview: selectedCycle.is_preview,
    });
  }, [selectedCycle, cycleAccounts.fetch]);

  const refreshSelectedAccountWorkspace = useCallback(
    async (accountId: number | null | undefined) => {
      if (!selectedCycle || accountId == null) return;

      const matchingAccount = cycleAccounts.data?.accounts.find(
        (account) => account.account_id === accountId,
      );

      if (matchingAccount && accountHasWorkspaceData(matchingAccount)) {
        return;
      }

      const result = await accountWorkspace.fetch({
        accountId,
        from: selectedCycle.from,
        to: selectedCycle.to,
        referenceTime: selectedCycle.started_at,
        futurePreview: selectedCycle.is_preview,
      });

      if (result.success && result.data) {
        setWorkspaceCache((current) => ({
          ...current,
          [workspaceCacheKey(selectedCycle.id, accountId)]:
            result.data as CycleAccountWorkspace,
        }));
      }
    },
    [accountWorkspace, cycleAccounts.data?.accounts, selectedCycle],
  );

  useEffect(() => {
    cycles.fetch();
  }, [cycles.fetch]);

  useEffect(() => {
    if (selectedCycle) {
      refreshAccounts();
    }
  }, [selectedCycle, refreshAccounts]);

  useEffect(() => {
    if (!reconcileState) return;

    reconcile.fetchPreview(reconcileState.accountId, cycleParams);
  }, [cycleParams, reconcile.fetchPreview, reconcileState]);

  useEffect(() => {
    if (!detailState) return;

    accountDetail.inspectAccount(
      detailState.account_id,
      flattenAccountTimesheets(detailState).map((timesheet) => timesheet.id),
    );
  }, [accountDetail.inspectAccount, detailState]);

  useEffect(() => {
    const nextParams = new URLSearchParams();

    if (selectedCycleId != null) {
      nextParams.set('cycle', String(selectedCycleId));
    }
    if (activeTab !== 'accounts') {
      nextParams.set('tab', activeTab);
    }
    if (searchQuery.trim()) {
      nextParams.set('search', searchQuery.trim());
    }
    if (accountFilter !== 'all') {
      nextParams.set('filter', accountFilter);
    }
    if (selectedAccountId != null) {
      nextParams.set('account', String(selectedAccountId));
    }

    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [
    accountFilter,
    activeTab,
    searchParams,
    searchQuery,
    selectedAccountId,
    selectedCycleId,
    setSearchParams,
  ]);

  const handleCycleSelect = useCallback(
    (cycle: InvoiceCycle) => {
      cycleAccounts.clear();
      accountWorkspace.clearError();
      setSelectedCycleId(cycle.id);
      setActiveTab('accounts');
      setSelectedAccountId(null);
      setConfirmCreateState(null);
      setCycleAnalysisState(null);
      setReconcileState(null);
      setDetailState(null);
      cycleAnalysis.clear();
      assignTimesheets.clearError();
      removeLineItem.clearError();
      createInvoice.clearError();
      voidInvoice.clearError();
      reconcile.clear();
      accountDetail.clear();
    },
    [
      accountDetail.clear,
      accountWorkspace.clearError,
      assignTimesheets.clearError,
      cycleAccounts.clear,
      cycleAnalysis.clear,
      removeLineItem.clearError,
      createInvoice.clearError,
      voidInvoice.clearError,
      reconcile.clear,
    ],
  );

  const handleAssignTimesheets = useCallback(
    async (
      _account: CycleAccountWorkspace,
      invoiceId: number,
      timesheetIds: number[],
    ) => {
      assignTimesheets.clearError();
      const result = await assignTimesheets.assign({ invoiceId, timesheetIds });

      if (result.success) {
        refreshAccounts();
        void refreshSelectedAccountWorkspace(_account.account_id);
        return true;
      }

      return false;
    },
    [
      assignTimesheets.assign,
      assignTimesheets.clearError,
      refreshAccounts,
      refreshSelectedAccountWorkspace,
    ],
  );

  const handleRemoveLineItem = useCallback(
    async (_account: CycleAccountWorkspace, timesheet: CycleTimesheet) => {
      if (!timesheet.invoice_line_item_id) return;

      const confirmed = window.confirm(
        `Remove line item for timesheet #${timesheet.id} (${timesheet.worker_name || 'Unknown'})?\n\nThis will delete the line item from Stripe and update the invoice total.`,
      );
      if (!confirmed) return;

      removeLineItem.clearError();
      const result = await removeLineItem.remove(
        timesheet.invoice_line_item_id,
      );

      if (result.success) {
        refreshAccounts();
        void refreshSelectedAccountWorkspace(_account.account_id);
      } else {
        window.alert(result.error || 'Failed to remove line item');
      }
    },
    [
      removeLineItem.remove,
      removeLineItem.clearError,
      refreshAccounts,
      refreshSelectedAccountWorkspace,
    ],
  );

  const handleCreateInvoice = useCallback(
    async (account: CycleAccountWorkspace) => {
      if (!selectedCycle) return;

      createInvoice.clearError();
      const result = await createInvoice.fetchPreview({
        accountId: account.account_id,
        referenceTime: selectedCycle.started_at,
        from: selectedCycle.from,
        to: selectedCycle.to,
        futurePreview: selectedCycle.is_preview,
      });

      if (result.success) {
        setConfirmCreateState({ account });
      }
    },
    [selectedCycle, createInvoice.fetchPreview, createInvoice.clearError],
  );

  const handleConfirmCreateInvoice = useCallback(
    async (groupKeys: string[]) => {
      if (!selectedCycle || !confirmCreateState) return;

      const result = await createInvoice.create({
        accountId: confirmCreateState.account.account_id,
        referenceTime: selectedCycle.started_at,
        from: selectedCycle.from,
        to: selectedCycle.to,
        futurePreview: selectedCycle.is_preview,
        groupKeys,
      });

      if (result.success) {
        setConfirmCreateState(null);
        refreshAccounts();
        void refreshSelectedAccountWorkspace(
          confirmCreateState.account.account_id,
        );
      }
    },
    [
      confirmCreateState,
      createInvoice.create,
      refreshAccounts,
      refreshSelectedAccountWorkspace,
      selectedCycle,
    ],
  );

  const handleVoidInvoice = useCallback(
    async (account: CycleAccountWorkspace, invoice: CycleInvoiceSection) => {
      const confirmed = window.confirm(
        `Void invoice #${invoice.id} for ${account.account_name}? This will void the invoice in Stripe and reset linked timesheets.`,
      );
      if (!confirmed) return;

      voidInvoice.clearError();
      const success = await voidInvoice.voidInvoice(invoice.id);

      if (success) {
        refreshAccounts();
        void refreshSelectedAccountWorkspace(account.account_id);
      }
    },
    [
      voidInvoice.voidInvoice,
      voidInvoice.clearError,
      refreshAccounts,
      refreshSelectedAccountWorkspace,
    ],
  );

  const handleInspectInvoice = useCallback(
    (account: CycleAccountWorkspace) => {
      accountDetail.clear();
      setDetailState(account);
    },
    [accountDetail.clear],
  );

  const handleDetailClose = useCallback(() => {
    accountDetail.clear();
    setDetailState(null);
  }, [accountDetail.clear]);

  const handleReconcile = useCallback(
    (account: CycleAccountWorkspace) => {
      reconcile.clear();
      setReconcileState({
        accountId: account.account_id,
        accountName: account.account_name,
      });
    },
    [reconcile.clear],
  );

  const handleReconcileClose = useCallback(() => {
    if (reconcile.executing) return;

    reconcile.clear();
    setReconcileState(null);
  }, [reconcile.clear, reconcile.executing]);

  const handleReconcileExecute = useCallback(async () => {
    if (!reconcileState) return;

    const executeResult = await reconcile.execute(
      reconcileState.accountId,
      cycleParams,
    );

    if (executeResult.success) {
      refreshAccounts();
      void refreshSelectedAccountWorkspace(reconcileState.accountId);
    }
  }, [
    cycleParams,
    reconcile.execute,
    reconcileState,
    refreshAccounts,
    refreshSelectedAccountWorkspace,
  ]);

  const handleRunCycleAnalysis = useCallback(async () => {
    if (!selectedCycle) return;

    cycleAnalysis.clear();
    setCycleAnalysisState({ cycleLabel: selectedCycle.date_label });
    await cycleAnalysis.fetch({
      from: selectedCycle.from,
      to: selectedCycle.to,
      referenceTime: selectedCycle.started_at,
      futurePreview: selectedCycle.is_preview,
    });
  }, [cycleAnalysis.clear, cycleAnalysis.fetch, selectedCycle]);

  const handleCloseCycleAnalysis = useCallback(() => {
    cycleAnalysis.clear();
    setCycleAnalysisState(null);
  }, [cycleAnalysis.clear]);

  const handleRunStuckHeldTimesheets = useCallback(async () => {
    stuckHeldTimesheets.clear();
    setIsStuckHeldModalOpen(true);
    await stuckHeldTimesheets.fetch();
  }, [stuckHeldTimesheets.clear, stuckHeldTimesheets.fetch]);

  const handleCloseStuckHeldTimesheets = useCallback(() => {
    stuckHeldTimesheets.clear();
    resolveStuckHeldTimesheet.clearError();
    setIsStuckHeldModalOpen(false);
  }, [resolveStuckHeldTimesheet.clearError, stuckHeldTimesheets.clear]);

  const handleResolveStuckHeldTimesheet = useCallback(
    async (
      timesheet: StuckHeldTimesheet,
      action: 'clear_hold' | 'restore_line_item',
    ) => {
      const confirmed = window.confirm(
        action === 'clear_hold'
          ? `Clear hold for timesheet #${timesheet.id}?`
          : `Restore the missing line item for timesheet #${timesheet.id} onto invoice #${timesheet.held_invoice_id}?`,
      );
      if (!confirmed) return;

      resolveStuckHeldTimesheet.clearError();
      const result = await resolveStuckHeldTimesheet.resolve({
        timesheetId: timesheet.id,
        action,
      });

      if (result.success) {
        await stuckHeldTimesheets.fetch();
        refreshAccounts();
        void refreshSelectedAccountWorkspace(timesheet.account_id);
      } else {
        window.alert(result.error || 'Failed to resolve stuck held timesheet');
      }
    },
    [
      refreshAccounts,
      resolveStuckHeldTimesheet.clearError,
      resolveStuckHeldTimesheet.resolve,
      refreshSelectedAccountWorkspace,
      stuckHeldTimesheets.fetch,
    ],
  );

  const handleRunEmptyDraftInvoices = useCallback(async () => {
    emptyDraftInvoices.clear();
    setIsEmptyDraftModalOpen(true);
    await emptyDraftInvoices.fetch();
  }, [emptyDraftInvoices.clear, emptyDraftInvoices.fetch]);

  const handleCloseEmptyDraftInvoices = useCallback(() => {
    emptyDraftInvoices.clear();
    softDeleteEmptyDraftInvoice.clearError();
    setIsEmptyDraftModalOpen(false);
  }, [emptyDraftInvoices.clear, softDeleteEmptyDraftInvoice.clearError]);

  const handleSoftDeleteEmptyDraftInvoice = useCallback(
    async (invoice: EmptyDraftInvoice) => {
      const confirmed = window.confirm(
        `Soft delete empty draft invoice #${invoice.id}?`,
      );
      if (!confirmed) return;

      softDeleteEmptyDraftInvoice.clearError();
      const result = await softDeleteEmptyDraftInvoice.remove(invoice.id);

      if (result.success) {
        await emptyDraftInvoices.fetch();
        refreshAccounts();
      } else {
        window.alert(
          result.error || 'Failed to soft delete empty draft invoice',
        );
      }
    },
    [
      emptyDraftInvoices.fetch,
      refreshAccounts,
      softDeleteEmptyDraftInvoice.clearError,
      softDeleteEmptyDraftInvoice.remove,
    ],
  );

  const handleSelectAccount = useCallback(
    async (account: CycleAccount) => {
      setSelectedAccountId(account.account_id);
      accountWorkspace.clearError();

      if (!selectedCycle || accountHasWorkspaceData(account)) {
        return;
      }

      const cacheKey = workspaceCacheKey(selectedCycle.id, account.account_id);
      if (workspaceCache[cacheKey]) {
        return;
      }

      const result = await accountWorkspace.fetch({
        accountId: account.account_id,
        from: selectedCycle.from,
        to: selectedCycle.to,
        referenceTime: selectedCycle.started_at,
        futurePreview: selectedCycle.is_preview,
      });

      if (result.success && result.data) {
        setWorkspaceCache((current) => ({
          ...current,
          [cacheKey]: result.data as CycleAccountWorkspace,
        }));
      }
    },
    [
      accountWorkspace,
      selectedCycle,
      workspaceCache,
      setWorkspaceCache,
      setSelectedAccountId,
    ],
  );

  const accounts = useMemo(
    () => cycleAccounts.data?.accounts || [],
    [cycleAccounts.data],
  );
  const sortedAccounts = useMemo(
    () =>
      [...accounts].sort((left, right) => {
        const leftStatus = accountStatusMeta(left);
        const rightStatus = accountStatusMeta(right);

        if (leftStatus.priority !== rightStatus.priority) {
          return leftStatus.priority - rightStatus.priority;
        }

        if (actionableMissingCount(left) !== actionableMissingCount(right)) {
          return actionableMissingCount(right) - actionableMissingCount(left);
        }

        if (accountInvoiceCount(left) !== accountInvoiceCount(right)) {
          return accountInvoiceCount(left) - accountInvoiceCount(right);
        }

        return `${left.customer_name} ${left.account_name}`.localeCompare(
          `${right.customer_name} ${right.account_name}`,
        );
      }),
    [accounts],
  );
  const filteredAccounts = useMemo(() => {
    const normalizedQuery = deferredSearchQuery.trim().toLowerCase();
    return sortedAccounts.filter((account) => {
      const accountName = account.account_name.toLowerCase();
      const customerName = account.customer_name.toLowerCase();
      const accountId = String(account.account_id);
      const matchesSearch =
        accountName.includes(normalizedQuery) ||
        customerName.includes(normalizedQuery) ||
        accountId.includes(normalizedQuery);

      if (!matchesSearch) return false;

      if (accountFilter === 'all') return true;
      if (accountFilter === 'no_invoice')
        return accountInvoiceCount(account) === 0;

      const isComplete = accountIsComplete(account);

      if (accountFilter === 'complete') return isComplete;
      return !isComplete;
    });
  }, [accountFilter, deferredSearchQuery, sortedAccounts]);
  const selectedAccount = useMemo(
    () =>
      selectedAccountId == null
        ? null
        : filteredAccounts.find(
            (account) => account.account_id === selectedAccountId,
          ) || null,
    [filteredAccounts, selectedAccountId],
  );
  const selectedAccountWorkspace = useMemo(() => {
    if (!selectedAccount || !selectedCycle) return null;
    if (accountHasWorkspaceData(selectedAccount)) return selectedAccount;

    return (
      workspaceCache[
        workspaceCacheKey(selectedCycle.id, selectedAccount.account_id)
      ] || null
    );
  }, [selectedAccount, selectedCycle, workspaceCache]);
  const actionError =
    assignTimesheets.error ||
    removeLineItem.error ||
    createInvoice.error ||
    voidInvoice.error ||
    accountWorkspace.error;
  const isRefreshingCycleAccounts =
    selectedCycle != null &&
    cycleAccounts.loading &&
    cycleAccounts.data != null;
  const isSelectedAccountLoading =
    selectedAccountId != null &&
    accountWorkspace.loadingAccountId === selectedAccountId;

  useEffect(() => {
    if (!selectedCycle) return;

    setWorkspaceCache((current) => {
      let changed = false;
      const next = { ...current };

      accounts.forEach((account) => {
        if (!accountHasWorkspaceData(account)) return;

        const key = workspaceCacheKey(selectedCycle.id, account.account_id);
        if (next[key] === account) return;

        next[key] = account;
        changed = true;
      });

      return changed ? next : current;
    });
  }, [accounts, selectedCycle]);

  useEffect(() => {
    if (
      !selectedAccount ||
      !selectedCycle ||
      accountHasWorkspaceData(selectedAccount) ||
      selectedAccountWorkspace ||
      isSelectedAccountLoading
    ) {
      return;
    }

    void handleSelectAccount(selectedAccount);
  }, [
    handleSelectAccount,
    isSelectedAccountLoading,
    selectedAccount,
    selectedAccountWorkspace,
    selectedCycle,
  ]);

  const handlePasswordSubmit = useCallback(
    (event?: React.FormEvent | React.MouseEvent) => {
      if (event) event.preventDefault();

      if (password === INVOICE_HEALTH_PASSWORD) {
        setIsAuthenticated(true);
        setAuthError('');
        localStorage.setItem(INVOICE_HEALTH_AUTH_KEY, 'true');
        return;
      }

      setAuthError('Incorrect password');
      setPassword('');
    },
    [password],
  );

  if (!isAuthenticated) {
    return (
      <Page title="Invoice Health" size="sm">
        <Card>
          <Card.Section>
            <form onSubmit={handlePasswordSubmit}>
              <Stack vertical gap={16}>
                <div>
                  <Heading>Invoice Health</Heading>
                  <Small>Enter the page password to continue.</Small>
                </div>
                <div>
                  <input
                    autoFocus
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Enter password"
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: 8,
                      border: '1px solid #d0d5dd',
                      background: '#fff',
                      fontSize: 14,
                      color: '#344054',
                    }}
                  />
                </div>
                {authError && (
                  <div style={{ color: '#b42318', fontSize: 14 }}>
                    {authError}
                  </div>
                )}
                <Stack justify="end">
                  <Button
                    a11yLabel="Unlock invoice health"
                    label="Continue"
                    type="submit"
                    onClick={handlePasswordSubmit}
                  />
                </Stack>
              </Stack>
            </form>
          </Card.Section>
        </Card>
      </Page>
    );
  }

  return (
    <Page title="Invoice Health" size="xl">
      <Stack vertical gap={20}>
        <Card>
          <Card.Section>
            <Stack vertical gap={12}>
              <div>
                <Heading>Invoice Health</Heading>
                <Small>
                  Select a cycle to review approved timesheets by account
                </Small>
              </div>

              {cycles.loading ? (
                <Skeleton.Body lines={2} />
              ) : cycles.error ? (
                <div style={{ color: '#b42318', fontSize: 14 }}>
                  {cycles.error}
                </div>
              ) : (
                <Stack vertical gap={12}>
                  <CycleDropdown
                    cycles={cycles.cycles}
                    cycleData={cycleAccounts.data}
                    selectedCycle={selectedCycle}
                    onSelect={handleCycleSelect}
                  />
                  <Tabs
                    selected={activeTab === 'accounts' ? 0 : 1}
                    tabs={[
                      {
                        a11yLabel: 'Accounts',
                        label: selectedCycle
                          ? `Accounts (${filteredAccounts.length})`
                          : 'Accounts',
                        onAction: () => {},
                      },
                      {
                        a11yLabel: 'Diagnostics',
                        label: 'Diagnostics',
                        onAction: () => {},
                      },
                    ]}
                    onSelect={(tab) =>
                      setActiveTab(tab === 0 ? 'accounts' : 'diagnostics')
                    }
                  />
                  {activeTab === 'diagnostics' && (
                    <Small>
                      Secondary cleanup tools and cycle-wide checks live here so
                      the main account triage stays lighter.
                    </Small>
                  )}
                </Stack>
              )}
            </Stack>
          </Card.Section>
        </Card>

        {actionError && (
          <Card>
            <Card.Section>
              <div style={{ color: '#b42318', fontSize: 14 }}>
                {actionError}
              </div>
            </Card.Section>
          </Card>
        )}

        {activeTab === 'accounts' && !selectedCycle && !cycles.loading && (
          <Card>
            <Card.Section>
              <EmptyState title="Select a cycle to load accounts and approved timesheets" />
            </Card.Section>
          </Card>
        )}

        {activeTab === 'accounts' &&
          selectedCycle &&
          cycleAccounts.loading &&
          !cycleAccounts.data && (
            <LoadingState text="Loading account list..." />
          )}

        {activeTab === 'accounts' &&
          isRefreshingCycleAccounts &&
          selectedCycle && (
            <Card>
              <Card.Section>
                <LoadingState
                  overlayColor="white"
                  text={`Refreshing ${selectedCycle.date_label} account list...`}
                  css={{ minHeight: 120 }}
                />
              </Card.Section>
            </Card>
          )}

        {activeTab === 'accounts' && selectedCycle && cycleAccounts.error && (
          <Card>
            <Card.Section>
              <div style={{ color: '#b42318', fontSize: 14 }}>
                {cycleAccounts.error}
              </div>
            </Card.Section>
          </Card>
        )}

        {activeTab === 'accounts' && selectedCycle && cycleAccounts.data && (
          <>
            {accounts.length === 0 ? (
              <Card>
                <Card.Section>
                  <EmptyState title="No approved timesheets found in this cycle" />
                </Card.Section>
              </Card>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isPhone ? '1fr' : '280px minmax(0, 1fr)',
                  gap: 20,
                  alignItems: 'start',
                }}
              >
                <AccountListPanel
                  accounts={filteredAccounts}
                  selectedAccountId={selectedAccountId}
                  totalAccountsCount={accounts.length}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  accountFilter={accountFilter}
                  onAccountFilterChange={setAccountFilter}
                  onSelect={handleSelectAccount}
                />
                {selectedAccountId == null ? (
                  <Card>
                    <Card.Section>
                      <EmptyState title="Select an account to inspect invoices and timesheets" />
                    </Card.Section>
                  </Card>
                ) : isSelectedAccountLoading ? (
                  <Card>
                    <Card.Section>
                      <LoadingState
                        overlayColor="white"
                        text={`Loading account ${selectedAccountId} workspace...`}
                        css={{ minHeight: 240 }}
                      />
                    </Card.Section>
                  </Card>
                ) : selectedAccountWorkspace ? (
                  <AccountCard
                    key={`${selectedCycle.id}-${selectedAccountWorkspace.account_id}`}
                    account={selectedAccountWorkspace}
                    loadingAssignInvoiceId={assignTimesheets.loadingInvoiceId}
                    loadingRemoveItemId={removeLineItem.loadingItemId}
                    loadingCreateAccountId={createInvoice.loadingAccountId}
                    loadingVoidInvoiceId={voidInvoice.loadingInvoiceId}
                    onAssignTimesheets={handleAssignTimesheets}
                    onRemoveLineItem={handleRemoveLineItem}
                    onInspectInvoice={handleInspectInvoice}
                    onCreateInvoice={handleCreateInvoice}
                    onVoidInvoice={handleVoidInvoice}
                    onReconcile={handleReconcile}
                  />
                ) : selectedAccount ? (
                  <Card>
                    <Card.Section>
                      <Stack vertical gap={8}>
                        <EmptyState title="Account details are not available yet" />
                        <Small>
                          Select the account again to retry loading the
                          workspace.
                        </Small>
                      </Stack>
                    </Card.Section>
                  </Card>
                ) : (
                  <Card>
                    <Card.Section>
                      <Stack vertical gap={8}>
                        <EmptyState title="Selected account is hidden by the current search or filter" />
                        <Small>
                          Clear or adjust the left-side filters to bring that
                          account back into view.
                        </Small>
                      </Stack>
                    </Card.Section>
                  </Card>
                )}
              </div>
            )}
          </>
        )}

        {activeTab === 'diagnostics' && (
          <Stack vertical gap={20}>
            <Card>
              <Card.Section>
                <Stack vertical gap={6}>
                  <Body weight="semibold">Diagnostics workspace</Body>
                  <Small>
                    Keep cycle-wide checks and global cleanup tools here so the
                    main account workspace stays focused on high-volume triage.
                  </Small>
                </Stack>
              </Card.Section>
            </Card>

            <Card>
              <Card.Section>
                <Stack justify="apart" align="start" gap={16}>
                  <Stack vertical gap={6}>
                    <Body weight="semibold">Cycle diagnostics</Body>
                    <Small>
                      {selectedCycle
                        ? `Run invoice analysis for ${selectedCycle.date_label}.`
                        : 'Select a cycle first to run invoice analysis.'}
                    </Small>
                  </Stack>
                  {selectedCycle && (
                    <Chip
                      color="default"
                      label={selectedCycle.date_label}
                      size="small"
                    />
                  )}
                </Stack>
              </Card.Section>
              <Card.Section>
                <Stack gap={12} wrap>
                  <Button
                    a11yLabel="Run invoice analysis"
                    appearance="secondary"
                    disabled={!selectedCycle}
                    label="Run invoice analysis"
                    isLoading={cycleAnalysis.loading}
                    type="button"
                    onClick={handleRunCycleAnalysis}
                  />
                </Stack>
              </Card.Section>
            </Card>

            <Card>
              <Card.Section>
                <Stack vertical gap={6}>
                  <Body weight="semibold">Global cleanup</Body>
                  <Small>
                    These tools scan across all invoice data and are not limited
                    to the selected cycle.
                  </Small>
                </Stack>
              </Card.Section>
              <Card.Section>
                <Stack gap={12} wrap>
                  <Button
                    a11yLabel="Find stuck held timesheets"
                    appearance="secondary"
                    label="Find stuck held timesheets"
                    isLoading={stuckHeldTimesheets.loading}
                    type="button"
                    onClick={handleRunStuckHeldTimesheets}
                  />
                  <Button
                    a11yLabel="Find empty draft invoices"
                    appearance="secondary"
                    label="Find empty draft invoices"
                    isLoading={emptyDraftInvoices.loading}
                    type="button"
                    onClick={handleRunEmptyDraftInvoices}
                  />
                </Stack>
              </Card.Section>
            </Card>
          </Stack>
        )}

        <ReconcileModal
          isOpen={reconcileState != null}
          accountName={reconcileState?.accountName || null}
          accountId={reconcileState?.accountId || null}
          preview={reconcile.preview}
          loading={reconcile.loading}
          executing={reconcile.executing}
          error={reconcile.error}
          onClose={handleReconcileClose}
          onExecute={handleReconcileExecute}
        />
        <StuckHeldTimesheetsModal
          isOpen={isStuckHeldModalOpen}
          loading={stuckHeldTimesheets.loading}
          error={resolveStuckHeldTimesheet.error || stuckHeldTimesheets.error}
          report={stuckHeldTimesheets.report}
          loadingTimesheetId={resolveStuckHeldTimesheet.loadingTimesheetId}
          onClose={handleCloseStuckHeldTimesheets}
          onResolve={handleResolveStuckHeldTimesheet}
        />
        <EmptyDraftInvoicesModal
          isOpen={isEmptyDraftModalOpen}
          loading={emptyDraftInvoices.loading}
          error={softDeleteEmptyDraftInvoice.error || emptyDraftInvoices.error}
          report={emptyDraftInvoices.report}
          loadingInvoiceId={softDeleteEmptyDraftInvoice.loadingInvoiceId}
          onClose={handleCloseEmptyDraftInvoices}
          onDelete={handleSoftDeleteEmptyDraftInvoice}
        />
        <ConfirmCreateInvoiceModal
          state={confirmCreateState}
          groups={createInvoice.groups}
          loading={createInvoice.loadingAccountId != null}
          error={createInvoice.error}
          onCancel={() => {
            createInvoice.clearError();
            setConfirmCreateState(null);
          }}
          onConfirm={handleConfirmCreateInvoice}
        />
        <AccountDetailModal
          isOpen={detailState != null}
          loading={accountDetail.loading}
          error={accountDetail.error}
          account={detailState}
          accountDetail={accountDetail.accountDetail}
          onClose={handleDetailClose}
        />
        <CycleAnalysisModal
          isOpen={cycleAnalysisState != null}
          cycleLabel={cycleAnalysisState?.cycleLabel || null}
          loading={cycleAnalysis.loading}
          error={cycleAnalysis.error}
          analysis={cycleAnalysis.analysis}
          onClose={handleCloseCycleAnalysis}
        />
      </Stack>
    </Page>
  );
};

export default InvoiceHealth;
